/**
 * Live preview iframe of the public site — the admin half of the Forge
 * `vv-*` postMessage protocol (site half: apps/site PreviewBridge.astro).
 *
 * HDM messages (adapted from VV's block/card protocol to whole sections):
 *   → vv-section-fields  { items: [{ id: 'section-3', fields: {path: val} }] }
 *   → vv-section-reorder { order: ['section-2', 'section-0', …] } — []
 *     is legal and hides every deployed section
 *   → vv-record-fields / vv-record-order — Lists mode: patch, sort + hide
 *     the open list's records (cards of ANY list stay clickable)
 *   → vv-scroll-to / vv-select / vv-hover
 *   → vv-comment-mode / vv-pins / vv-focus-pin — review "Comment" mode:
 *     clicks drop numbered pins; the pins to draw are posted whole (docs/REVIEW.md)
 *   ← vv-preview-ready / vv-doc-height / vv-scroll-pos / vv-edit-click /
 *     vv-navigate (edit-click kind 'field' carries the clicked [data-f] dot
 *     path; kind 'record' the [data-rec] + its [data-rf] field; kind
 *     'chrome' a nav/footer click; vv-navigate an internal link's path)
 *   ← vv-comment-click { anchor, viewportWidth } / vv-pin-click { id }
 *
 * Protocol laws carried over from FORGE.md — do not "fix" these:
 *   - Message handlers are registered ONCE ([] deps); every prop they need
 *     is mirrored into a ref (reading React state from a stale closure is
 *     the classic never-saves bug's cousin).
 *   - The admin scrolls its own pane INSTANTLY, never smooth — Chrome
 *     cancels programmatic smooth scrolls when the main thread is busy.
 *   - The iframe is sized to the document height the site reports; the
 *     frame pane INSIDE this component is the one scroller (the root fills
 *     its parent: chrome bar / banner / frame pane / footer). Heights reset
 *     only when the iframe actually reloads — width switches keep the last
 *     measurement until the site reposts, otherwise every toggle blows the
 *     pane out to INITIAL_HEIGHT.
 */

import { useEffect, useRef, useState } from 'react';
import { ALLOWED_SITE_ORIGINS, SITE_ORIGIN } from '~/lib/config';
import type { ReviewAnchor } from '~/lib/review';

export type PreviewWidth = 'mobile' | 'tablet' | 'desktop' | 'wide';

/** One review pin to draw in Comment mode. */
export interface PreviewPin {
  id: string;
  /** Printed on the pin: '3', or 'D3' for a dev note. */
  label: string;
  kind: 'change' | 'dev';
  status: 'open' | 'resolved';
  anchor: ReviewAnchor;
}

const PRESETS: Record<PreviewWidth, { w: number; label: string }> = {
  mobile: { w: 375, label: 'Phone' },
  tablet: { w: 768, label: 'Tablet' },
  desktop: { w: 1280, label: 'Desktop' },
  wide: { w: 1920, label: 'Wide' },
};

/** Deliberately huge initial height so nothing clips if vv-doc-height is
 * delayed or dropped. */
const INITIAL_HEIGHT = 6000;

export interface SectionFieldsItem {
  id: string;
  fields: Record<string, string | number>;
}

export interface EditClickPayload {
  kind: 'section' | 'field' | 'record' | 'chrome';
  target: string;
  /** [data-f] dot path when kind === 'field' (e.g. 'cards.2.blurb');
   * the record's [data-rf] name when kind === 'record'. */
  field?: string;
  sectionType?: string;
  /** '<list>:<id>' when kind === 'record'. For kind 'chrome', `target`
   * is 'nav' | 'footer' (site chrome, edited in Nav & footer). */
  record?: string;
}

/** Live display values for one record's [data-rf] leaves. */
export interface RecordFieldsItem {
  rec: string;
  fields: Record<string, string | number | string[]>;
}

/** One list's shown records ('<list>:<id>') in editor order. */
export interface RecordOrder {
  list: string;
  order: string[];
}

const asOrders = (v: RecordOrder | RecordOrder[] | null | undefined): RecordOrder[] =>
  Array.isArray(v) ? v : v ? [v] : [];

/** One deployed section as reported by vv-preview-ready (document order). */
export interface PreviewSectionInfo {
  id: string;
  type: string;
}

interface Props {
  /** Public-site path, e.g. '/', '/about', '/services/social'. */
  href: string;
  /** Live field drafts per deployed section. */
  sectionFields?: SectionFieldsItem[];
  /** Deployed-section ids in the editor's current order. An EMPTY array is
   * meaningful (hide everything) and is still posted. */
  sectionOrder?: string[];
  /** CSS selector to scroll to (usually the selection). */
  scrollTo?: string;
  /** CSS selector to ring as "selected"; null clears. */
  selectedSelector?: string | null;
  /** CSS selector to hover-outline (admin list hover); null clears. */
  hoverSelector?: string | null;
  onEditClick?: (payload: EditClickPayload) => void;
  /** Called with the deployed build's section inventory whenever the iframe
   * (re)loads — lets the editor re-bind its preview ids to the real DOM. */
  onInventory?: (sections: PreviewSectionInfo[]) => void;
  initialWidth?: PreviewWidth;
  /** True when the page has unpublished structure changes (new sections)
   * that live-patching can't show. */
  structureChanged?: boolean;
  /** Lists mode: live display values per record. */
  recordFields?: RecordFieldsItem[];
  /** Each list's shown records in editor order — records left out are
   * hidden in the preview. One entry per list (a page can carry several). */
  recordOrder?: RecordOrder | RecordOrder[] | null;
  /** Records ('<list>:<id>') present in the deployed build, per (re)load. */
  onRecordInventory?: (records: string[]) => void;
  /** An internal link outside the sections (nav, footer) was clicked — the
   * editor should switch to that page (the iframe itself never navigates). */
  onNavigate?: (path: string) => void;
  /** Extra banner under the chrome bar. */
  notice?: React.ReactNode;
  /** Controlled screen size (Comment mode pins belong to one). Omit and the
   * preview keeps its own. */
  width?: PreviewWidth;
  onWidthChange?: (w: PreviewWidth) => void;
  /** Comment mode: a click drops a review pin instead of editing. */
  commentMode?: boolean;
  /** Pins to draw — already filtered to this page + screen size. */
  pins?: PreviewPin[];
  activePinId?: string | null;
  /** The unsaved pin between a click and "Add comment". */
  draftAnchor?: ReviewAnchor | null;
  /** Scroll to a pin; the nonce re-triggers the same pin. */
  focusPin?: { id: string; nonce: number } | null;
  onCommentClick?: (c: { anchor: ReviewAnchor; viewportWidth: number }) => void;
  onPinClick?: (id: string) => void;
}

export default function PagePreview({
  href,
  sectionFields,
  sectionOrder,
  scrollTo,
  selectedSelector,
  hoverSelector,
  onEditClick,
  onInventory,
  initialWidth = 'desktop',
  structureChanged = false,
  recordFields,
  recordOrder,
  onRecordInventory,
  onNavigate,
  notice,
  width: widthProp,
  onWidthChange,
  commentMode = false,
  pins,
  activePinId,
  draftAnchor,
  focusPin,
  onCommentClick,
  onPinClick,
}: Props) {
  const [ownWidth, setOwnWidth] = useState<PreviewWidth>(initialWidth);
  const width = widthProp ?? ownWidth;
  const setWidth = (w: PreviewWidth) => {
    setOwnWidth(w);
    onWidthChange?.(w);
  };
  const [iframeKey, setIframeKey] = useState(1);
  const [refreshedAt, setRefreshedAt] = useState<number>(() => Date.now());
  const [, forceTick] = useState(0);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);

  // Ref mirrors — the message handlers register once with [] deps.
  const onEditClickRef = useRef(onEditClick);
  onEditClickRef.current = onEditClick;
  const onInventoryRef = useRef(onInventory);
  onInventoryRef.current = onInventory;
  const sectionFieldsRef = useRef(sectionFields);
  sectionFieldsRef.current = sectionFields;
  const sectionOrderRef = useRef(sectionOrder);
  sectionOrderRef.current = sectionOrder;
  const selectedSelectorRef = useRef(selectedSelector);
  selectedSelectorRef.current = selectedSelector;
  const scrollToRef = useRef(scrollTo);
  scrollToRef.current = scrollTo;
  const recordFieldsRef = useRef(recordFields);
  recordFieldsRef.current = recordFields;
  const recordOrderRef = useRef(recordOrder);
  recordOrderRef.current = recordOrder;
  const onRecordInventoryRef = useRef(onRecordInventory);
  onRecordInventoryRef.current = onRecordInventory;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const commentModeRef = useRef(commentMode);
  commentModeRef.current = commentMode;
  const pinsRef = useRef(pins);
  pinsRef.current = pins;
  const activePinRef = useRef(activePinId);
  activePinRef.current = activePinId;
  const draftRef = useRef(draftAnchor);
  draftRef.current = draftAnchor;
  const focusPinRef = useRef(focusPin);
  focusPinRef.current = focusPin;
  const onCommentClickRef = useRef(onCommentClick);
  onCommentClickRef.current = onCommentClick;
  const onPinClickRef = useRef(onPinClick);
  onPinClickRef.current = onPinClick;

  // Width-only scaling: iframe renders at the preset's logical width and is
  // CSS-scaled to fit the pane. Never upscaled past 1.
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const compute = () => {
      const paneWidth = frame.clientWidth;
      setScale(Math.min(1, paneWidth / PRESETS[width].w));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(frame);
    return () => ro.disconnect();
  }, [width]);

  // Dynamic iframe height from vv-doc-height. Reset ONLY on a real reload
  // (iframeKey) — width switches keep the last measurement until the site
  // reposts, so the pane doesn't jump to 6000px on every toggle.
  const [docHeight, setDocHeight] = useState<number>(INITIAL_HEIGHT);
  useEffect(() => {
    setDocHeight(INITIAL_HEIGHT);
  }, [iframeKey]);
  // A pin focus request stays "live" for a few seconds: switching screen
  // size makes the page reflow for a while (images, fonts), and the pin's
  // position moves with it — so every height change inside the window
  // re-aims at the pin.
  const focusLiveUntil = useRef(0);
  const refocusTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (!ALLOWED_SITE_ORIGINS.has(e.origin)) return;
      if (e.data?.type !== 'vv-doc-height') return;
      const h = Number(e.data.height);
      if (!(Number.isFinite(h) && h > 0)) return;
      setDocHeight(h);
      const wanted = focusPinRef.current;
      if (wanted && commentModeRef.current && Date.now() < focusLiveUntil.current) {
        window.clearTimeout(refocusTimer.current);
        refocusTimer.current = window.setTimeout(() => {
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'vv-focus-pin', id: wanted.id },
            SITE_ORIGIN,
          );
        }, 150);
      }
    }
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(refocusTimer.current);
    };
  }, []);

  // Main protocol handler: ready handshake, click-to-edit, scroll replies.
  useEffect(() => {
    function post(msg: Record<string, unknown>) {
      iframeRef.current?.contentWindow?.postMessage(msg, SITE_ORIGIN);
    }
    function onMessage(e: MessageEvent) {
      if (!ALLOWED_SITE_ORIGINS.has(e.origin)) return;
      if (!e.data) return;

      if (e.data.type === 'vv-preview-ready') {
        // Hand the deployed section inventory up FIRST — the editor may
        // re-bind ids, and its keyed effects re-post corrected state after.
        if (Array.isArray(e.data.sections) && onInventoryRef.current) {
          const inv: PreviewSectionInfo[] = (e.data.sections as unknown[])
            .map((s) => {
              const rec = (s ?? {}) as Record<string, unknown>;
              return { id: String(rec.id ?? ''), type: String(rec.type ?? '') };
            })
            .filter((s) => s.id !== '');
          onInventoryRef.current(inv);
        }
        if (onRecordInventoryRef.current) {
          onRecordInventoryRef.current(
            Array.isArray(e.data.records) ? (e.data.records as unknown[]).map(String) : [],
          );
        }
        // Push the full current state, in order. The order list is posted
        // whenever it EXISTS — empty means hide everything.
        if (sectionOrderRef.current) {
          post({ type: 'vv-section-reorder', order: sectionOrderRef.current });
        }
        if (sectionFieldsRef.current?.length) {
          post({ type: 'vv-section-fields', items: sectionFieldsRef.current });
        }
        for (const o of asOrders(recordOrderRef.current)) {
          post({ type: 'vv-record-order', ...o });
        }
        if (recordFieldsRef.current?.length) {
          post({ type: 'vv-record-fields', items: recordFieldsRef.current });
        }
        if (scrollToRef.current) {
          const sel = scrollToRef.current;
          // Delayed so layout settles before the site measures the target.
          setTimeout(() => post({ type: 'vv-scroll-to', anchor: sel }), 250);
        }
        // ALWAYS sent (even null) — clears stale rings on reload.
        post({ type: 'vv-select', selector: selectedSelectorRef.current ?? null });
        // Review state, whole: mode, then the pins, then (after layout
        // settles) the pin a deep link or inbox click asked for.
        post({ type: 'vv-comment-mode', on: commentModeRef.current });
        post({
          type: 'vv-pins',
          pins: pinsRef.current ?? [],
          activeId: activePinRef.current ?? null,
          draft: draftRef.current ? { anchor: draftRef.current } : null,
        });
        // Comment mode only: a stale focus must never scroll an Edit-mode load.
        const wanted = commentModeRef.current ? focusPinRef.current : null;
        if (wanted) setTimeout(() => post({ type: 'vv-focus-pin', id: wanted.id }), 500);
        return;
      }

      if (e.data.type === 'vv-comment-click') {
        const a = e.data.anchor as ReviewAnchor | undefined;
        if (a && a.v === 1) {
          onCommentClickRef.current?.({ anchor: a, viewportWidth: Number(e.data.viewportWidth) || 0 });
        }
        return;
      }

      if (e.data.type === 'vv-pin-click') {
        if (typeof e.data.id === 'string') onPinClickRef.current?.(e.data.id);
        return;
      }

      if (e.data.type === 'vv-edit-click' && onEditClickRef.current) {
        const field = typeof e.data.field === 'string' && e.data.field ? e.data.field : undefined;
        onEditClickRef.current({
          kind:
            e.data.kind === 'field' || e.data.kind === 'record' || e.data.kind === 'chrome'
              ? e.data.kind
              : 'section',
          target: String(e.data.target ?? ''),
          field,
          sectionType: e.data.sectionType ? String(e.data.sectionType) : undefined,
          record: typeof e.data.record === 'string' && e.data.record ? e.data.record : undefined,
        });
        return;
      }

      if (e.data.type === 'vv-navigate') {
        if (typeof e.data.path === 'string') onNavigateRef.current?.(e.data.path);
        return;
      }

      if (e.data.type === 'vv-scroll-pos') {
        // The site reports where the target sits in its document (the
        // borderless iframe can't scroll itself). Scroll our nearest
        // scrollable ancestor — that's the frame pane wrapping the device
        // frame — so the element lands mid-pane, accounting for the
        // width-scale transform. The walk starts at the iframe so its
        // rect anchors the math regardless of pane padding.
        const top = Number(e.data.top);
        const elH = Number(e.data.height) || 0;
        if (!Number.isFinite(top)) return;
        const iframeEl = iframeRef.current;
        if (!iframeEl) return;
        let parent: HTMLElement | null = iframeEl.parentElement;
        while (parent) {
          const oy = getComputedStyle(parent).overflowY;
          if ((oy === 'auto' || oy === 'scroll') && parent.scrollHeight > parent.clientHeight)
            break;
          parent = parent.parentElement;
        }
        const scroller = parent ?? document.documentElement;
        // transform-origin is top left, so the iframe's rect top is
        // unaffected by the scale.
        const frameTopInScroller =
          iframeEl.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top +
          scroller.scrollTop;
        const target =
          frameTopInScroller +
          top * scaleRef.current -
          scroller.clientHeight / 2 +
          (elH * scaleRef.current) / 2;
        // Instant, not smooth — Chrome cancels programmatic smooth scrolls
        // when the main thread is busy, which leaves the pane sitting at 0.
        scroller.scrollTo({ top: Math.max(0, target) });
        return;
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Prop-change pushes. JSON keys keep the effects stable across array
  // identity churn from re-renders.
  const fieldsKey = sectionFields?.length ? JSON.stringify(sectionFields) : '';
  useEffect(() => {
    if (!sectionFields?.length) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'vv-section-fields', items: sectionFields },
      SITE_ORIGIN,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsKey, iframeKey]);

  // Empty array ≠ undefined: [] must still post (it hides every section).
  const orderKey = sectionOrder ? JSON.stringify(sectionOrder) : '';
  useEffect(() => {
    if (!sectionOrder) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'vv-section-reorder', order: sectionOrder },
      SITE_ORIGIN,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey, iframeKey]);

  const recFieldsKey = recordFields?.length ? JSON.stringify(recordFields) : '';
  useEffect(() => {
    if (!recordFields?.length) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'vv-record-fields', items: recordFields },
      SITE_ORIGIN,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recFieldsKey, iframeKey]);

  const recOrderKey = recordOrder ? JSON.stringify(recordOrder) : '';
  useEffect(() => {
    for (const o of asOrders(recordOrder)) {
      iframeRef.current?.contentWindow?.postMessage({ type: 'vv-record-order', ...o }, SITE_ORIGIN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recOrderKey, iframeKey]);

  // Review pushes (docs/REVIEW.md). Mode first, then the pins — the pins
  // list is the WHOLE set to draw, so a change of page / screen size just
  // posts a different list.
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'vv-comment-mode', on: commentMode },
      SITE_ORIGIN,
    );
  }, [commentMode, iframeKey]);

  const pinsKey = pins ? JSON.stringify(pins) : '';
  const draftKey = draftAnchor ? JSON.stringify(draftAnchor) : '';
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'vv-pins',
        pins: pins ?? [],
        activeId: activePinId ?? null,
        draft: draftAnchor ? { anchor: draftAnchor } : null,
      },
      SITE_ORIGIN,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinsKey, activePinId, draftKey, commentMode, iframeKey]);

  // Scroll to a pin: a few timed passes, plus a re-aim on every page-height
  // change for the next 3s (see focusLiveUntil) — after a screen-size switch
  // the pin keeps moving until the page has finished relaying out.
  const focusNonce = focusPin?.nonce;
  useEffect(() => {
    const wanted = focusPinRef.current;
    if (!wanted || !commentModeRef.current) return;
    focusLiveUntil.current = Date.now() + 3000;
    const send = () =>
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'vv-focus-pin', id: wanted.id },
        SITE_ORIGIN,
      );
    const timers = [250, 700, 1400].map((ms) => window.setTimeout(send, ms));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [focusNonce]);

  // Re-center on the scroll target once a scale change SETTLES. Selecting
  // something opens the props panel, which animates this pane narrower
  // (240ms) — a vv-scroll-pos answered mid-animation was scaled by a
  // stale factor and overshot (often to the bottom of the page).
  useEffect(() => {
    if (!scrollToRef.current) return;
    const t = window.setTimeout(() => {
      const anchor = scrollToRef.current;
      if (anchor) {
        iframeRef.current?.contentWindow?.postMessage({ type: 'vv-scroll-to', anchor }, SITE_ORIGIN);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [scale]);

  useEffect(() => {
    if (!scrollTo) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'vv-scroll-to', anchor: scrollTo },
      SITE_ORIGIN,
    );
  }, [scrollTo]);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'vv-select', selector: selectedSelector ?? null },
      SITE_ORIGIN,
    );
  }, [selectedSelector]);

  // Hover sync: posts on every change; the cleanup clears the outline when
  // the value goes away (including unmount mid-hover).
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'vv-hover', selector: hoverSelector ?? null },
      SITE_ORIGIN,
    );
    if (!hoverSelector) return;
    return () => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'vv-hover', selector: null },
        SITE_ORIGIN,
      );
    };
  }, [hoverSelector]);

  // "Refreshed Xs ago" heartbeat.
  useEffect(() => {
    const t = window.setInterval(() => forceTick((n) => n + 1), 15_000);
    return () => window.clearInterval(t);
  }, []);

  const fullUrl = `${SITE_ORIGIN}${href.startsWith('/') ? '' : '/'}${href}`;
  // previewMode=1 activates the bridge in PreviewBridge.astro; preview=<key>
  // is a cache-buster and doubles as the React remount key.
  const previewSrc = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}previewMode=1&preview=${iframeKey}`;

  // Computed per render on purpose — the 15s tick above IS the update; a
  // useMemo keyed on refreshedAt cached "just now" forever.
  const agoSeconds = Math.floor((Date.now() - refreshedAt) / 1000);
  const agoMinutes = Math.floor(agoSeconds / 60);
  const refreshedAgo =
    agoSeconds < 60
      ? 'just now'
      : agoMinutes < 60
        ? `${agoMinutes}m ago`
        : `${Math.floor(agoMinutes / 60)}h ago`;

  return (
    <section
      className={`content-preview content-preview--${width}`}
      // Fills the editor cell: chrome / banner / frame pane (the ONE
      // scroller) / footer. minHeight:0 lets the pane shrink inside grid
      // and flex parents instead of forcing the cell taller.
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <div
        className="content-preview__chrome"
        style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}
      >
        <span className="content-preview__lights" style={{ display: 'inline-flex', gap: 5 }}>
          <span />
          <span />
          <span />
        </span>
        <span className="content-preview__url" style={{ flex: 1, padding: '4px 10px' }}>
          {fullUrl.replace(/^https:\/\//, '')}
        </span>
        <span className="content-preview__widths" role="tablist" style={{ display: 'inline-flex', gap: 4 }}>
          {(Object.keys(PRESETS) as PreviewWidth[]).map((w) => (
            <button
              key={w}
              role="tab"
              aria-selected={width === w}
              className={width === w ? 'is-active' : ''}
              title={`${PRESETS[w].label} (${PRESETS[w].w}px)`}
              onClick={() => setWidth(w)}
              style={{
                border: '1px solid var(--line)',
                borderRadius: 6,
                padding: '4px 8px',
                fontSize: 11,
                cursor: 'pointer',
                background: width === w ? 'var(--primary)' : 'transparent',
                color: width === w ? 'var(--card)' : 'var(--text-muted)',
              }}
            >
              {PRESETS[w].label}
            </button>
          ))}
        </span>
        <button
          type="button"
          onClick={() => {
            setIframeKey((k) => k + 1);
            setRefreshedAt(Date.now());
          }}
          title="Reload the preview from the last published build"
          style={{
            border: '1px solid var(--line)',
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: 11.5,
            cursor: 'pointer',
            background: 'transparent',
            color: 'var(--text-muted)',
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {structureChanged && (
        <div
          style={{
            padding: '8px 14px',
            fontSize: 12.5,
            background: 'rgba(79, 176, 79, 0.1)',
            borderBottom: '1px solid rgba(79, 176, 79, 0.3)',
            color: 'var(--primary)',
            flex: 'none',
          }}
        >
          Added/removed sections preview after the next <strong>Publish</strong> — text and
          photo edits to existing sections preview live.
        </div>
      )}

      {notice && (
        <div
          style={{
            padding: '8px 14px',
            fontSize: 12.5,
            background: 'rgba(79, 176, 79, 0.1)',
            borderBottom: '1px solid rgba(79, 176, 79, 0.3)',
            color: 'var(--primary)',
            flex: 'none',
          }}
        >
          {notice}
        </div>
      )}

      {/* The frame pane: the only scroller. frameRef doubles as the width
          reference for the fit-to-pane scale. */}
      <div
        ref={frameRef}
        className="content-preview__frame"
        style={{ position: 'relative', flex: '1 1 0%', minHeight: 0, overflow: 'auto' }}
      >
        {/* Device frame: sized to the SCALED iframe so the pane gets the
            right scroll height, centered horizontally (auto margins work
            under both block and flex parents). */}
        <div
          style={{
            width: PRESETS[width].w * scale,
            height: docHeight * scale,
            margin: '0 auto',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={previewSrc}
            title="Public site preview"
            loading="lazy"
            // Deliberate fallback: if doc-height messaging hiccups the iframe
            // can still scroll internally — better a tiny scrollbar than half
            // the page invisible.
            scrolling="auto"
            style={{
              width: PRESETS[width].w,
              height: docHeight,
              border: 'none',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              background: '#fff',
            }}
          />
        </div>
      </div>

      <div
        className="content-preview__footer"
        style={{
          padding: '6px 14px',
          fontSize: 11,
          color: 'var(--text-muted)',
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid var(--line-soft)',
          flex: 'none',
        }}
      >
        <span>Refreshed {refreshedAgo}</span>
        <a href={fullUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--text-muted)' }}>
          Open in new tab ↗
        </a>
      </div>
    </section>
  );
}
